import { lastNonNull, median, type Severity } from '@ijoin/components';
import { GateSampleRes, QueueSampleRes } from '@ijoin/hooks';

export type Health = 'error' | 'idle' | 'success' | 'warning';

export type IntegrationSeries = {
  avgProcMs: (null | number)[];
  batchSize: (null | number)[];
  inFlight: (null | number)[];
  integrationId: string;
  integrationName: string;
  latestBatchSize: number;
  latestParallelCalls: number;
  queueDepth: (null | number)[];
  saturationPct: (null | number)[];
  windowProcCount: number;
  windowProcMsSum: number;
};

export type IntegrationSummaryRow = {
  avgMs: number;
  cap: number;
  health: Health;
  hint: string;
  inFlight: number;
  integrationId: string;
  integrationName: string;
  isSlowOutlier: boolean;
  parallelCalls: number;
  queueDepth: number;
  saturationPct: number;
};

export const RANGE_OPTIONS = [
  { hours: 1, label: 'Last 1 hour' },
  { hours: 6, label: 'Last 6 hours' },
  { hours: 24, label: 'Last 24 hours' },
  { hours: 24 * 7, label: 'Last 7 days' }
];

export const HEALTH_LABEL: Record<string, string> = {
  error: 'Critical',
  idle: 'Idle',
  success: 'Healthy',
  warning: 'Pressure'
};

export const HEALTH_SEVERITY: Record<string, Severity> = {
  error: 'error',
  idle: 'info',
  success: 'success',
  warning: 'warning'
};

export const computeHealth = ({
  cap,
  inFlight,
  queueDepth,
  saturationPct
}: {
  cap: number;
  inFlight: number;
  queueDepth: number;
  saturationPct: number;
}): Health => {
  const denom = cap > 0 ? cap : 1;

  if (saturationPct >= 90 && queueDepth >= denom * 5) return 'error';
  if (saturationPct >= 75 && queueDepth >= denom * 2) return 'warning';
  if (queueDepth > 0 && inFlight === 0) return 'idle';

  return 'success';
};

export const computeHint = (row: {
  cap: number;
  health: Health;
  inFlight: number;
  parallelCalls: number;
  queueDepth: number;
}): string => {
  if (row.health === 'success') {
    return 'Within healthy limits.';
  }

  if (row.health === 'idle') {
    return `${row.queueDepth} message${row.queueDepth === 1 ? '' : 's'} held but no in-flight processing. Check consumer health.`;
  }

  if (row.cap < row.parallelCalls) {
    return `Cap (rateLimitBatchSize=${row.cap}) is below the SOAP throttle (${row.parallelCalls}). Consider raising rateLimitBatchSize.`;
  }

  if (row.cap >= row.parallelCalls && row.parallelCalls > 0) {
    return `Pegged at SOAP throttle (rateLimitMaxParallelCalls=${row.parallelCalls}). Raise it (or scale downstream) to add headroom.`;
  }

  return 'Pegged at the rate-limit cap.';
};

export const buildSummaryRows = (
  series: IntegrationSeries[]
): IntegrationSummaryRow[] => {
  const avgsForOutlier = series
    .map(s =>
      s.windowProcCount > 0 ? s.windowProcMsSum / s.windowProcCount : 0
    )
    .filter(v => v > 0);
  const baseline = median(avgsForOutlier);

  return series.map(s => {
    const inFlight = lastNonNull(s.inFlight, 0);
    const queueDepth = lastNonNull(s.queueDepth, 0);
    const cap = lastNonNull(s.batchSize, 0) || s.latestBatchSize;
    const parallelCalls = s.latestParallelCalls;
    const saturationPct = cap > 0 ? Math.round((inFlight / cap) * 100) : 0;
    const health = computeHealth({ cap, inFlight, queueDepth, saturationPct });
    const avgMs =
      s.windowProcCount > 0
        ? Math.round(s.windowProcMsSum / s.windowProcCount)
        : 0;

    return {
      avgMs,
      cap,
      health,
      hint: computeHint({
        cap,
        health,
        inFlight,
        parallelCalls,
        queueDepth
      }),
      inFlight,
      integrationId: s.integrationId,
      integrationName: s.integrationName,
      isSlowOutlier: baseline > 0 && avgMs >= baseline * 2,
      parallelCalls,
      queueDepth,
      saturationPct
    };
  });
};

export const groupSamples = (samples: GateSampleRes[]) => {
  const tsSet = new Set<number>();

  for (const s of samples) {
    tsSet.add(new Date(s.sampledAt).getTime());
  }

  const timestamps = [...tsSet].sort((a, b) => a - b);
  const tsIndex = new Map(timestamps.map((t, i) => [t, i]));

  const byInt = new Map<string, IntegrationSeries>();

  for (const s of samples) {
    const i = tsIndex.get(new Date(s.sampledAt).getTime());

    if (i === undefined) continue;

    let series = byInt.get(s.integrationId);

    if (!series) {
      series = {
        avgProcMs: Array(timestamps.length).fill(null),
        batchSize: Array(timestamps.length).fill(null),
        inFlight: Array(timestamps.length).fill(null),
        integrationId: s.integrationId,
        integrationName: s.integrationName ?? s.integrationId,
        latestBatchSize: s.rateLimitBatchSize,
        latestParallelCalls: s.rateLimitMaxParallelCalls ?? 0,
        queueDepth: Array(timestamps.length).fill(null),
        saturationPct: Array(timestamps.length).fill(null),
        windowProcCount: 0,
        windowProcMsSum: 0
      };
      byInt.set(s.integrationId, series);
    }

    series.batchSize[i] = s.rateLimitBatchSize;
    series.inFlight[i] = s.inFlight;
    series.queueDepth[i] = s.queueDepth;
    series.latestBatchSize = s.rateLimitBatchSize;
    series.latestParallelCalls = s.rateLimitMaxParallelCalls ?? 0;
    series.saturationPct[i] =
      s.rateLimitBatchSize > 0
        ? Math.round((s.inFlight / s.rateLimitBatchSize) * 100)
        : null;
    series.avgProcMs[i] =
      s.procCount > 0 ? Math.round(s.procMsSum / s.procCount) : null;
    series.windowProcCount += s.procCount;
    series.windowProcMsSum += s.procMsSum;
  }

  return {
    series: [...byInt.values()],
    timestamps: timestamps.map(t => new Date(t))
  };
};

export const groupQueueSamples = (samples: QueueSampleRes[]) => {
  const sorted = [...samples].sort(
    (a, b) => new Date(a.sampledAt).getTime() - new Date(b.sampledAt).getTime()
  );

  return {
    delayed: sorted.map(s => s.delayed),
    inFlight: sorted.map(s => s.inFlight),
    messages: sorted.map(s => s.messages),
    oldestMessageAgeSec: sorted.map(s => s.oldestMessageAgeSec),
    timestamps: sorted.map(s => new Date(s.sampledAt))
  };
};

export const bucketByMinute = (
  samples: GateSampleRes[],
  field: 'defers' | 'hits',
  stack: string
) => {
  const integrations = new Map<string, string>();
  const byBucket = new Map<number, Map<string, number>>();

  for (const s of samples) {
    const bucket = Math.floor(new Date(s.sampledAt).getTime() / 60_000);

    integrations.set(s.integrationId, s.integrationName ?? s.integrationId);

    let intMap = byBucket.get(bucket);

    if (!intMap) {
      intMap = new Map();
      byBucket.set(bucket, intMap);
    }

    intMap.set(s.integrationId, (intMap.get(s.integrationId) ?? 0) + s[field]);
  }

  const buckets = [...byBucket.keys()].sort((a, b) => a - b);
  const labels = buckets.map(b =>
    new Date(b * 60_000).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    })
  );

  const series = [...integrations.entries()].map(([id, name]) => ({
    data: buckets.map(b => byBucket.get(b)?.get(id) ?? 0),
    label: name,
    stack
  }));

  return { labels, series };
};

const MISS_REASON_LABEL: Record<string, string> = {
  missesInstanceCap: 'Instance cap (per-process share)',
  missesQueueCap: 'Queue cap (per-(integration, queue))',
  missesTotalFull: 'Total cap (rateLimitBatchSize)'
};

export const bucketMissesByReason = (samples: GateSampleRes[]) => {
  const reasons: (
    | 'missesInstanceCap'
    | 'missesQueueCap'
    | 'missesTotalFull'
  )[] = ['missesTotalFull', 'missesQueueCap', 'missesInstanceCap'];
  const byBucket = new Map<number, Record<(typeof reasons)[number], number>>();

  for (const s of samples) {
    const bucket = Math.floor(new Date(s.sampledAt).getTime() / 60_000);
    let row = byBucket.get(bucket);

    if (!row) {
      row = { missesInstanceCap: 0, missesQueueCap: 0, missesTotalFull: 0 };
      byBucket.set(bucket, row);
    }

    row.missesInstanceCap += s.missesInstanceCap ?? 0;
    row.missesQueueCap += s.missesQueueCap ?? 0;
    row.missesTotalFull += s.missesTotalFull ?? 0;
  }

  const buckets = [...byBucket.keys()].sort((a, b) => a - b);
  const labels = buckets.map(b =>
    new Date(b * 60_000).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    })
  );

  const series = reasons.map(reason => ({
    data: buckets.map(b => byBucket.get(b)?.[reason] ?? 0),
    label: MISS_REASON_LABEL[reason],
    stack: 'misses'
  }));

  return { labels, series };
};
